import {
  SUGGESTED_INSTRUCTIONS_ACCUSE_OTHERS_STEALING,
  SUGGESTED_INSTRUCTIONS_COMBATIVE_BEHAVIOR_AT_PERSONAL_CARE,
  SUGGESTED_INSTRUCTIONS_EATS_NON_EDIBLE_OBJECTS,
  SUGGESTED_INSTRUCTIONS_FIRE_SETTING,
  SUGGESTED_INSTRUCTIONS_HIDING_ITEMS,
  SUGGESTED_INSTRUCTIONS_HOARDING,
  SUGGESTED_INSTRUCTIONS_INTIMIDATING_THREATNING,
  SUGGESTED_INSTRUCTIONS_LAW_BREAKING_ACTIVITIES,
  SUGGESTED_INSTRUCTIONS_LEFT_HOME_LOST,
  SUGGESTED_INSTRUCTIONS_MANIAC_EPISODES,
  SUGGESTED_INSTRUCTIONS_NIGHT_DISRUPTIONS,
  SUGGESTED_INSTRUCTIONS_OBSESSIVE_BEHAVIORS,
  SUGGESTED_INSTRUCTIONS_REPITITIVE_BEHAVIORS,
  SUGGESTED_INSTRUCTIONS_RAPID_MOOD_SWINGS,
  SUGGESTED_INSTRUCTIONS_SEXUAL_ACTING,
  SUGGESTED_INSTRUCTIONS_SEXUAL_VIOLENCE,
  SUGGESTED_INSTRUCTIONS_SMOKING,
  SUGGESTED_INSTRUCTIONS_TAKES_OTHER_PERSONS_ITEMS,
  SUGGESTED_INSTRUCTIONS_TOILETING_MENSES,
  SUGGESTED_INSTRUCTIONS_UNSAFE_COOKING,
  SUGGESTED_INSTRUCTIONS_USES_OFFENSIVE_LANGUGAGE,
  SUGGESTED_INSTRUCTIONS_VERBAL_NOISES,
  SUGGESTED_INSTRUCTIONS_VERBALLY_ABUSIVE,
  SUGGESTED_INSTRUCTIONS_YELLING_SCREAMING
} from "../../../constants/carePlanConstants";

export type PsychSocialBehaviorFieldConfig = {
  sectionCategory: string;
  title: string;
  fieldPrefix: string;
  definition: string;
  potentialCauses: string[];
  suggestedInstructions: any[];
};

export const PSYCH_SOCIAL_BEHAVIOR_REGISTRY: PsychSocialBehaviorFieldConfig[] = [
  {
    sectionCategory: "Symptoms of Distress",
    title: "Obsessive regarding own health or body functions",
    fieldPrefix: "obsessiveHealthBodyFunctions",
    definition: "Persistently seeks medical attention or shows obsessive concern with body functions. This is intended for extreme behavior rather than regular concern about ongoing health care or body-function care.",
    potentialCauses: [
      "May be due to dementia or another medical condition",
      "May be feeling insecure or unsafe",
      "May feel lost or be searching for something",
      "May need to use the bathroom",
      "May be constipated",
      "May have a bladder or urinary tract infection",
      "May be in pain or physically uncomfortable",
      "May be hungry or thirsty"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_OBSESSIVE_BEHAVIORS
  },
  {
    sectionCategory: "Symptoms of Distress",
    title: "Non-health related repetitive anxious complaints or questions",
    fieldPrefix: "repetitiveAnxiousComplaints",
    definition: "Persistently seeks attention or reassurance about schedules, meals, laundry, clothing, relationships, or similar topics, and may repeatedly ask for help or reassurance.",
    potentialCauses: [
      "May be related to anxiety or depression",
      "May be having difficulty with transition",
      "May be related to dementia, brain injury, or another mental health diagnosis",
      "May be a medication side effect"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_REPITITIVE_BEHAVIORS
  },
  {
    sectionCategory: "Other Symptoms",
    title: "Manic episodes",
    fieldPrefix: "manicEpisodes",
    definition: "A distinct period of persistently elevated mood that may include inflated self-esteem, decreased need for sleep, nonstop talk, racing thoughts, impulsive pleasure-seeking, constant motion, or abrupt topic changes.",
    potentialCauses: [
      "May be related to a physical or mental health problem",
      "May be a medication side effect"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_MANIAC_EPISODES
  },
  {
    sectionCategory: "Other Symptoms",
    title: "Extreme/Rapid Mood Swings",
    fieldPrefix: "rapidMoodSwings",
    definition: "Mood changes that are so extreme, rapid, or serious that they disrupt the resident's everyday life.",
    potentialCauses: [
      "May be related to a physical or mental health problem"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_RAPID_MOOD_SWINGS
  },
  {
    sectionCategory: "Verbally Agitated/Aggressive",
    title: "Accuses others of stealing",
    fieldPrefix: "accusesOthersStealing",
    definition: "Makes false accusations that others have stolen items. This may happen when items are misplaced or when the resident wants items belonging to others.",
    potentialCauses: [
      "May be a symptom of dementia or Alzheimer's disease",
      "May be communicating frustration",
      "May be reacting to a new situation",
      "May be trying to communicate a need or emotion",
      "May be feeling rushed or out of control",
      "May be experiencing too much or too little stimulation",
      "May be related to confusion or disinhibition",
      "May have actually misplaced an item",
      "May have actually had an item stolen"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_ACCUSE_OTHERS_STEALING
  },
  {
    sectionCategory: "Verbally Agitated/Aggressive",
    title: "Inappropriate verbal noises causing distress to others",
    fieldPrefix: "inappropriateVerbalNoises",
    definition: "Disruptive sounds such as smacking lips, excessive noise, or repetitive utterances that cause distress to others.",
    potentialCauses: [
      "May be attempting to communicate a need or emotion",
      "May be in pain or discomfort",
      "May be trying to maintain a sense of control",
      "May be reacting to too much stimulation or too many cues",
      "May be a medication side effect",
      "May be related to confusion or disinhibition"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_VERBAL_NOISES
  },
  {
    sectionCategory: "Verbally Agitated/Aggressive",
    title: "Uses offensive language",
    fieldPrefix: "usesOffensiveLanguage",
    definition: "Uses foul, racist, sexist, or otherwise offensive language toward others.",
    potentialCauses: [
      "Could indicate a neurological disorder such as coprolalia",
      "May be communicating frustration",
      "May be reacting to a new situation",
      "May be trying to communicate a need or emotion",
      "May be feeling rushed or out of control",
      "May be experiencing too much or too little stimulation",
      "May be related to confusion or disinhibition"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_USES_OFFENSIVE_LANGUGAGE
  },
  {
    sectionCategory: "Verbally Agitated/Aggressive",
    title: "Verbally abusive",
    fieldPrefix: "verballyAbusiveBehavior",
    definition: "Threatens, demeans, or curses at caregivers, family, or others.",
    potentialCauses: [
      "May be related to confusion or disinhibition",
      "May have a legitimate reason to be angry but lack other problem-solving skills",
      "May be in physical pain or discomfort"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_VERBALLY_ABUSIVE
  },
  {
    sectionCategory: "Verbally Agitated/Aggressive",
    title: "Yelling/Screaming",
    fieldPrefix: "yellingScreaming",
    definition: "Loud vocalizations that are disruptive to the environment and not directed at others.",
    potentialCauses: [
      "May be in pain or physically uncomfortable",
      "May be related to confusion or disinhibition"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_YELLING_SCREAMING
  },
  {
    sectionCategory: "Physically Agitated/Aggressive",
    title: "Combative during personal care",
    fieldPrefix: "combativeDuringPersonalCare",
    definition: "During personal care, hits, shoves, scratches, bites, pinches, or engages in other behaviors that could injure others.",
    potentialCauses: [
      "May be communicating frustration or modesty",
      "May not see the need for the task",
      "May be reacting to care being provided too quickly",
      "May be trying to maintain personal control",
      "May be reacting to how care is being provided",
      "May be related to confusion or disinhibition",
      "May not need the task done for them",
      "May be frustrated by loss of independence"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_COMBATIVE_BEHAVIOR_AT_PERSONAL_CARE
  },
  {
    sectionCategory: "Physically Agitated/Aggressive",
    title: "Hiding Items",
    fieldPrefix: "hidingItems",
    definition: "Conceals items from others, whether the items belong to the resident or to other people.",
    potentialCauses: [
      "May be related to a lifelong habit from times of scarcity",
      "May be related to confusion or disinhibition"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_HIDING_ITEMS
  },
  {
    sectionCategory: "Physically Agitated/Aggressive",
    title: "Hoarding",
    fieldPrefix: "hoardingBehavior",
    definition: "Stores excessive amounts of food, medications, magazines, or similar items well beyond current needs.",
    potentialCauses: [
      "May be part of a general inability to organize and make decisions",
      "May be related to confusion or disinhibition",
      "May be related to growing up in the Depression era"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_HOARDING
  },
  {
    sectionCategory: "Physically Agitated/Aggressive",
    title: "Intimidating/Threatening (no physical contact)",
    fieldPrefix: "intimidatingThreatening",
    definition: "Attempts to force or deter someone else using threatening posture, gestures, or verbal threats without physical contact.",
    potentialCauses: [
      "May be using intimidation as a way to solve problems",
      "May have a legitimate reason to be angry but lack other problem-solving skills"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_INTIMIDATING_THREATNING
  },
  {
    sectionCategory: "Physically Agitated/Aggressive",
    title: "Rummages/takes belongings of others",
    fieldPrefix: "takesBelongingsOfOthers",
    definition: "Goes through or takes belongings, mail, drawers, or personal items of others without consent.",
    potentialCauses: [
      "May be related to confusion or disinhibition",
      "May be searching for a lost item"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_TAKES_OTHER_PERSONS_ITEMS
  },
  {
    sectionCategory: "Physically Agitated/Aggressive",
    title: "Deliberate Sexual Violence",
    fieldPrefix: "deliberateSexualViolence",
    definition: "Deliberate sexual violence such as rape, incest, pedophilia, or sexual violence toward family members or others.",
    potentialCauses: [
      "Likely related to long-term pathology",
      "May involve legal restrictions or treatment requirements"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_SEXUAL_VIOLENCE
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Eats non-edible substances/objects (Pica)",
    fieldPrefix: "eatsNonEdibleObjects",
    definition: "Persistent eating of non-nutritive substances for at least one month in a way that is developmentally inappropriate and not culturally sanctioned.",
    potentialCauses: [
      "May be associated with another developmental, cognitive, or brain disorder",
      "May reflect a nutritional deficiency"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_EATS_NON_EDIBLE_OBJECTS
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Deliberate Fire setting behaviors",
    fieldPrefix: "fireSettingBehaviors",
    definition: "Deliberately sets or attempts to set fires in wastebaskets, linens, drapes, or similar items. This does not include a careless smoker.",
    potentialCauses: [
      "Likely related to long-term pathology"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_FIRE_SETTING
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Inappropriate toileting/menses activity",
    fieldPrefix: "inappropriateToiletingMenses",
    definition: "Includes smearing or throwing feces, urinating in inappropriate places, shredding sanitary products, or similar behaviors.",
    potentialCauses: [
      "May be related to confusion or disinhibition",
      "May have a bladder or urinary tract infection",
      "May have painful menstruation"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_TOILETING_MENSES
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Left home and gotten lost",
    fieldPrefix: "leftHomeGottenLost",
    definition: "Gets lost in familiar surroundings and is unaware of the need to ask for help, whether walking, driving, or in a public place.",
    potentialCauses: [
      "May be related to confusion or disinhibition"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_LEFT_HOME_LOST
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Law breaking activities",
    fieldPrefix: "lawBreakingActivities",
    definition: "Activities that result in or place the resident at risk for law enforcement involvement, such as shoplifting, theft, trespassing, forgery, or disturbing the peace.",
    potentialCauses: [
      "Cognitive impairment may contribute to unintentional law breaking",
      "May be related to chemical dependency",
      "May be related to mental illness",
      "May involve relationships with unsafe people"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_LAW_BREAKING_ACTIVITIES
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Sexual acting out",
    fieldPrefix: "sexualActingOut",
    definition: "Sexual behavior that is contrary to usual social norms, such as masturbating in public, inappropriate touching, or exhibitionism intended to provoke reactions.",
    potentialCauses: [
      "Likely related to long-term pathology when the person knows the behavior is inappropriate",
      "May involve legal restrictions or treatment requirements"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_SEXUAL_ACTING
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Disrupts household at night when others are sleeping",
    fieldPrefix: "nightDisruptions",
    definition: "Being awake and calling out, or being out of bed and moving around the home while others are sleeping, in a way that requires intervention.",
    potentialCauses: [
      "May be related to confusion or disinhibition",
      "Could indicate a medical problem, especially if sudden in onset"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_NIGHT_DISRUPTIONS
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Unsafe cooking",
    fieldPrefix: "unsafeCooking",
    definition: "Leaves the stove on or shows evidence of burned pots, pans, food, or microwave fires.",
    potentialCauses: [
      "May be related to confusion or disinhibition"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_UNSAFE_COOKING
  },
  {
    sectionCategory: "Inappropriate or Unsafe Behavior",
    title: "Unsafe smoking",
    fieldPrefix: "unsafeSmoking",
    definition: "Burns cigarettes down to fingertips, smokes in unauthorized areas, fails to use ashtrays, or smokes while on oxygen.",
    potentialCauses: [
      "May be related to confusion or disinhibition",
      "Likely includes nicotine addiction"
    ],
    suggestedInstructions: SUGGESTED_INSTRUCTIONS_SMOKING
  }
];

